/**
 * Which city a contact belongs to (D853 phase A, analysis `docs/ai/analysis/D853_central_hybrid_automation_and_city.md` section 4) - governed key
 * `central.contactMarket`.
 *
 * The Central resolves the CITY (market) of every conversation with a layered resolver (`@vello/contact-market`), the strongest signal first. This
 * setting says which signals may be used, in which order of trust, when to ask the person, what to do with a city we do not serve, and which
 * phone-number prefixes make a city LIKELY (for ordering the "which city?" buttons only). The value:
 *
 *   `{ "signals": ["subject_record", "account", ...], "ask": { "beforeHandover": true, "maxAsks": 2 },
 *      "unservedCity": { "countDemand": true, "offerNotifyMe": true, "requireMarketingOptIn": true },
 *      "areaHints": { "orderOnly": true, "prefixes": { "<prefix>": ["<market id>"] } } }`
 *
 * Rules the owner fixed (2026-10-30) and this module enforces, so a version that breaks them cannot be published:
 *  - the record the contact is about (a ride, an order, a ticket) beats the account's home city: `subject_record` must precede `account`;
 *  - the person's own answer (`asked`) closes the list: it is what is left when no stronger signal exists, and the person can always change it;
 *  - a phone-number prefix NEVER decides a city (`areaHints.orderOnly` is always true): it only orders the buttons;
 *  - there is no IP-geolocation signal at all (the vocabulary of signals has none, and an unknown member refuses the value);
 *  - "Avise-me" (notify me when we arrive) needs an explicit marketing opt-in.
 *
 * A settings-kernel key (D393): platform and country scope, OVERRIDABLE (the country version replaces the platform's as a whole), versioned, effective-dated,
 * four eyes. An unreadable version fails CLOSED to the narrowest reading (only the person's own answer), never to a looser one. Neutral: no country, area
 * code scheme or city is named here - the prefixes are data the country pack proposes.
 */
import { COUNTRY_RE, effectiveBucket, isObj, onlyMembers, scopedSetting } from './central-ai-shared.js';
/** The governed key. */
export const CENTRAL_CONTACT_MARKET_KEY = 'central.contactMarket';
/**
 * The signals a market can be read from, from the strongest to the weakest. `subject_record`: the ride/order/ticket the contact is about;
 * `account`: the signed-in account's home city; `known_contact`: a verified phone or e-mail looked up (routing only, never revealed);
 * `page_context`: the page or partner embed the visitor came from; `device_location`: the device's position (a suggestion, with consent);
 * `asked`: the person picked it from the buttons.
 */
export const CONTACT_MARKET_SIGNALS = ['subject_record', 'account', 'known_contact', 'page_context', 'device_location', 'asked'];
/** Everything in force, the ride first, ask twice, count the demand, offer "notify me" with an opt-in, no prefixes (the country pack proposes them). */
export const DEFAULT_CENTRAL_CONTACT_MARKET = {
    signals: [...CONTACT_MARKET_SIGNALS],
    ask: { beforeHandover: true, maxAsks: 2 },
    unservedCity: { countDemand: true, offerNotifyMe: true, requireMarketingOptIn: true },
    areaHints: { orderOnly: true, prefixes: {} },
};
/** What fails closed: only the person's own answer decides, nothing is counted or offered. */
const NARROWEST = {
    signals: ['asked'],
    ask: { beforeHandover: true, maxAsks: 2 },
    unservedCity: { countDemand: false, offerNotifyMe: false, requireMarketingOptIn: true },
    areaHints: { orderOnly: true, prefixes: {} },
};
export const CENTRAL_CONTACT_MARKET_REASON_CODES = ['CONTACT_MARKET_GOVERNED', 'CONTACT_MARKET_DEFAULT', 'CONTACT_MARKET_INVALID'];
export const CENTRAL_CONTACT_MARKET_BOUNDS = { minAsks: 1, maxAsks: 3, maxPrefixes: 200, maxMarketsPerPrefix: 8 };
const PREFIX_RE = /^[0-9]{2,5}$/;
const MARKET_ID_RE = /^[a-z]{2}:[a-z0-9]+(-[a-z0-9]+)*$/;
/** The stable issue of a value that is not a contact-market value, or undefined when it is one. */
export function centralContactMarketIssue(v) {
    if (!isObj(v))
        return 'NOT_AN_OBJECT';
    const members = ['signals', 'ask', 'unservedCity', 'areaHints'];
    if (!onlyMembers(v, members) || !members.every((m) => m in v))
        return 'UNKNOWN_OR_MISSING_MEMBER';
    const signals = v.signals;
    if (!Array.isArray(signals) || signals.length < 1 || signals.length > CONTACT_MARKET_SIGNALS.length || new Set(signals).size !== signals.length
        || !signals.every((s) => typeof s === 'string' && CONTACT_MARKET_SIGNALS.includes(s)))
        return 'INVALID_SIGNALS';
    const at = (s) => signals.indexOf(s);
    if (at('account') >= 0 && at('subject_record') > at('account'))
        return 'SUBJECT_MUST_PRECEDE_ACCOUNT';
    if (at('asked') >= 0 && at('asked') !== signals.length - 1)
        return 'ASKED_MUST_BE_LAST';
    const ask = v.ask;
    if (!isObj(ask) || !onlyMembers(ask, ['beforeHandover', 'maxAsks']) || typeof ask.beforeHandover !== 'boolean' || !('maxAsks' in ask))
        return 'INVALID_ASK';
    if (typeof ask.maxAsks !== 'number' || !Number.isInteger(ask.maxAsks) || ask.maxAsks < CENTRAL_CONTACT_MARKET_BOUNDS.minAsks || ask.maxAsks > CENTRAL_CONTACT_MARKET_BOUNDS.maxAsks)
        return 'INVALID_MAX_ASKS';
    const un = v.unservedCity;
    if (!isObj(un) || !onlyMembers(un, ['countDemand', 'offerNotifyMe', 'requireMarketingOptIn']) || typeof un.countDemand !== 'boolean' || typeof un.offerNotifyMe !== 'boolean' || typeof un.requireMarketingOptIn !== 'boolean')
        return 'INVALID_UNSERVED_CITY';
    if (un.offerNotifyMe && !un.requireMarketingOptIn)
        return 'NOTIFY_ME_NEEDS_OPT_IN';
    const hints = v.areaHints;
    if (!isObj(hints) || !onlyMembers(hints, ['orderOnly', 'prefixes']) || !isObj(hints.prefixes) || typeof hints.orderOnly !== 'boolean')
        return 'INVALID_AREA_HINTS';
    if (hints.orderOnly !== true)
        return 'AREA_HINTS_MUST_ORDER_ONLY';
    const entries = Object.entries(hints.prefixes);
    if (entries.length > CENTRAL_CONTACT_MARKET_BOUNDS.maxPrefixes)
        return 'TOO_MANY_AREA_PREFIXES';
    for (const [prefix, markets] of entries) {
        if (!PREFIX_RE.test(prefix))
            return 'INVALID_AREA_PREFIX';
        if (!Array.isArray(markets) || markets.length < 1 || markets.length > CENTRAL_CONTACT_MARKET_BOUNDS.maxMarketsPerPrefix || new Set(markets).size !== markets.length || !markets.every((m) => typeof m === 'string' && MARKET_ID_RE.test(m)))
            return 'INVALID_AREA_MARKETS';
    }
    return undefined;
}
export function validCentralContactMarketValue(v) {
    return centralContactMarketIssue(v) === undefined;
}
/** Prefixes sorted, so equal meaning is equal content (one content hash). */
function normalise(v) {
    return {
        signals: [...v.signals],
        ask: { beforeHandover: v.ask.beforeHandover, maxAsks: v.ask.maxAsks },
        unservedCity: { countDemand: v.unservedCity.countDemand, offerNotifyMe: v.unservedCity.offerNotifyMe, requireMarketingOptIn: v.unservedCity.requireMarketingOptIn },
        areaHints: { orderOnly: true, prefixes: Object.fromEntries(Object.keys(v.areaHints.prefixes).sort().map((k) => [k, [...v.areaHints.prefixes[k]]])) },
    };
}
/** Build a valid version (platform or country scope). Throws a RangeError on a malformed scope ref or value. */
export function centralContactMarketSetting(input) {
    const issue = centralContactMarketIssue(input.value);
    if (issue !== undefined)
        throw new RangeError(`invalid contact-market policy: ${issue}`);
    return scopedSetting({ key: CENTRAL_CONTACT_MARKET_KEY, scope: input.scope, ...(input.scopeRef !== undefined ? { scopeRef: input.scopeRef } : {}), value: normalise(input.value), version: input.version, effectiveFrom: input.effectiveFrom, ...(input.effectiveTo !== undefined ? { effectiveTo: input.effectiveTo } : {}), mode: 'OVERRIDABLE' });
}
/**
 * The policy in force for `country` at `at`: the country's version if it has one, else the platform's, else the default. A version the reader cannot
 * parse fails closed to the narrowest reading.
 */
export function resolveCentralContactMarket(settings, country, at) {
    const invalid = () => ({ value: NARROWEST, reasonCode: 'CONTACT_MARKET_INVALID', policyVersion: `${CENTRAL_CONTACT_MARKET_KEY}@invalid-narrowest` });
    if (!COUNTRY_RE.test(country))
        return invalid();
    const own = effectiveBucket(settings, CENTRAL_CONTACT_MARKET_KEY, 'country', country, at);
    const found = own ?? effectiveBucket(settings, CENTRAL_CONTACT_MARKET_KEY, 'platform', undefined, at);
    if (found === undefined)
        return { value: DEFAULT_CENTRAL_CONTACT_MARKET, reasonCode: 'CONTACT_MARKET_DEFAULT', policyVersion: `${CENTRAL_CONTACT_MARKET_KEY}@default` };
    if (!validCentralContactMarketValue(found.value))
        return invalid();
    return { value: normalise(found.value), reasonCode: 'CONTACT_MARKET_GOVERNED', policyVersion: found.policyVersion };
}
//# sourceMappingURL=central-contact-market-policy.js.map